import { EyeIcon } from "@xola/icons";
import React, { memo, useCallback, useMemo } from "react";
import { Field, FieldType, FieldVisibility, FormResponse, Guest, Purchase, PurchaseItem } from "types";
import { RowData } from "./types";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { PhotoLightboxModal } from "@/components/Questionnaire/PhotoLightboxModal";
import { usePhotoField } from "@/components/Questionnaire/usePhotoField";
import { useModal } from "@/hooks/useVisibility";

interface PhotoQuestionnaireCellProps {
    value: FormResponse | null;
    data: RowData;
    headerName: string;
}

export const PhotoQuestionnaireCell = memo(({ value, data, headerName }: PhotoQuestionnaireCellProps) => {
    if (!value) {
        return null;
    }

    const guest = "guest" in data ? data.guest : undefined;
    const item = "item" in data ? data.item : undefined;
    // Get purchase from the item (ExperiencePurchaseItem has a purchase field)
    const purchase = item && "purchase" in item ? item.purchase : undefined;

    if (!item || !purchase) {
        return null;
    }

    const photoField = value.fields?.find((f) => f.label.trim().toLowerCase() === headerName.trim().toLowerCase());

    if (!photoField) {
        return null;
    }

    return <PhotoQuestionnaireCellInner fieldLabel={photoField.label} guest={guest} item={item} purchase={purchase} />;
});

PhotoQuestionnaireCell.displayName = "PhotoQuestionnaireCell";

interface PhotoQuestionnaireCellInnerProps {
    fieldLabel: string;
    guest?: Guest;
    item: PurchaseItem;
    purchase: Purchase;
}

const PhotoQuestionnaireCellInner = memo(({ fieldLabel, guest, item, purchase }: PhotoQuestionnaireCellInnerProps) => {
    const modal = useModal();

    // Create Field object for usePhotoField hook
    const field = useMemo(
        (): Field => ({
            id: fieldLabel,
            label: fieldLabel,
            type: FieldType.PHOTO,
            required: false,
            roster: false,
            readonly: false,
            seq: 0,
            organizerOnly: false,
            addToSavedViews: false,
            visibility: FieldVisibility.ALL,
        }),
        [fieldLabel],
    );

    const { photoPath, blob, blobUrl, isLoading, isError } = usePhotoField({ field, guest, item, purchase });

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            modal.show();
        },
        [modal],
    );

    const handleCloseModal = useCallback(() => modal.hide(), [modal]);

    if (isLoading) {
        return <span className="text-sm text-gray-dark">loading...</span>;
    }

    if (isError || !photoPath || !blobUrl) {
        return <span className="text-sm italic text-gray-light">no photo uploaded</span>;
    }

    return (
        <>
            <SecondaryButton size="small" className="mt-1 !p-2" title="View photo" onClick={handleClick}>
                <EyeIcon />
            </SecondaryButton>
            {modal.isVisible && (
                <PhotoLightboxModal
                    isOpen={modal.isVisible}
                    blobUrl={blobUrl}
                    blob={blob}
                    altText={field.label}
                    photoPath={photoPath}
                    onClose={handleCloseModal}
                />
            )}
        </>
    );
});

PhotoQuestionnaireCellInner.displayName = "PhotoQuestionnaireCellInner";
